// RBAC-Benutzerverwaltung (Backend: app/api/routes/office_rbac_users.py).
//
// Die Oberfläche zeigt nur, was der Server liefert und erlaubt. Ob eine Rolle
// vergeben werden darf, entscheidet ausschließlich das Backend über Permission-Keys;
// `vergebbare_rollen` im Standort-Katalog ist nur eine Anzeigehilfe.

import { api } from '../api';
import type { PermissionKey, ScopeKey } from './permissions.generated';

export interface RoleDef {
  key: string;
  label: string;
  scope_ceiling: ScopeKey;
  /** Obergrenze own/team: wirkt über Team-Mitgliedschaften, nicht über Standorte. */
  team_rolle: boolean;
  company_braucht_bereich: boolean;
  sensible_rechte: boolean;
}

export interface PermissionDef {
  key: PermissionKey;
  label: string;
  description: string;
  section_id: string;
  section_title: string;
  module_title: string;
  delegierbar_am_standort: boolean;
}

export interface StandortOption {
  id: number;
  name: string;
  /** Nur im Standort-Katalog. */
  vergebbare_rollen?: string[];
}

export interface Verantwortungsbereich {
  id: string;
  name: string;
  standort_ids: number[];
  mitglieder: number;
}

export interface Katalog {
  rollen: RoleDef[];
  scopes: Array<{ key: ScopeKey; label: string }>;
  funktionen: Array<{ key: string; label: string }>;
  permissions: PermissionDef[];
  standorte: StandortOption[];
  verantwortungsbereiche?: Verantwortungsbereich[];
}

export interface RollenZuweisung {
  id?: string;
  role_key: string;
  scope_type: 'global' | 'location';
  standort_ids: number[];
  scope_override: ScopeKey | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active?: boolean;
}

export interface RbacBenutzer {
  account_id: string;
  person_id: number | null;
  email: string;
  vorname: string;
  nachname: string;
  aktiv: boolean;
  mfa_enabled: boolean;
  mfa_exception: boolean;
  /** Besitzt Rechte, für die die 2FA-Richtlinie greift (sofern eingeschaltet). */
  sensible_rechte: boolean;
  rollen: RollenZuweisung[];
  standort_team_ids: number[];
  standort_ids: number[];
  /** Standorte mit gesperrtem Zugang: Rollen bleiben, wirken dort aber nicht (F27). */
  gesperrte_standort_ids: number[];
  altrollen: Array<{ rolle: string; standort_id: number | null }>;
  funktionen?: string[];
  verantwortungsbereich_ids?: string[];
  weitere_team_ids?: string[];
}

export interface ZuordnungenPayload {
  rollen?: RollenZuweisung[];
  standort_team_ids?: number[];
  funktionen?: string[];
  verantwortungsbereich_ids?: string[];
  standort_ids?: number[];
}

export interface Override {
  id: string;
  account_id: string;
  permission_key: PermissionKey;
  effect: 'grant' | 'revoke';
  scope: ScopeKey | null;
  standort_id: number | null;
  limits: { max_value_eur?: number; per?: string } | null;
  reason: string;
  granted_by: string;
  valid_until: string | null;
  created_at: string | null;
  active: boolean;
}

export interface OverridePayload {
  permission_key: string;
  effect: 'grant' | 'revoke';
  scope?: ScopeKey | null;
  standort_id?: number | null;
  limits?: { max_value_eur: number; per?: 'month' } | null;
  reason: string;
  valid_until?: string | null;
}

const reasonHeader = (reason: string) => ({ 'X-Sidigo-Reason': reason });

export const adminUsersApi = {
  katalog: () => api.get<Katalog>('/api/admin/benutzer/katalog'),
  list: () => api.get<RbacBenutzer[]>('/api/admin/benutzer'),
  get: (accountId: string) => api.get<RbacBenutzer>(`/api/admin/benutzer/${accountId}`),
  create: (payload: { email: string; vorname: string; nachname: string; password: string }) =>
    api.post<RbacBenutzer>('/api/admin/benutzer', payload),
  update: (accountId: string, payload: { email?: string; vorname?: string; nachname?: string }) =>
    api.patch<RbacBenutzer>(`/api/admin/benutzer/${accountId}`, payload),
  setActive: (accountId: string, aktiv: boolean) =>
    api.put<RbacBenutzer>(`/api/admin/benutzer/${accountId}/aktiv`, { aktiv }),
  setZuordnungen: (accountId: string, payload: ZuordnungenPayload) =>
    api.put<RbacBenutzer>(`/api/admin/benutzer/${accountId}/zuordnungen`, payload),
  createBereich: (payload: { name: string; standort_ids: number[] }) =>
    api.post<Verantwortungsbereich[]>('/api/admin/verantwortungsbereiche', payload),
  updateBereich: (id: string, payload: { name: string; standort_ids: number[] }) =>
    api.put<Verantwortungsbereich[]>(`/api/admin/verantwortungsbereiche/${id}`, payload),
  deleteBereich: (id: string) =>
    api.delete<Verantwortungsbereich[]>(`/api/admin/verantwortungsbereiche/${id}`),
  overrides: (accountId: string) => api.get<Override[]>(`/api/admin/benutzer/${accountId}/overrides`),
  createOverride: (accountId: string, payload: OverridePayload) =>
    api.post<Override>(`/api/admin/benutzer/${accountId}/overrides`, payload),
  deactivateOverride: (accountId: string, overrideId: string, reason: string) =>
    api.delete<Override>(`/api/admin/benutzer/${accountId}/overrides/${overrideId}`, {
      headers: reasonHeader(reason),
    }),
};

export const standortUsersApi = {
  katalog: () => api.get<Katalog>('/api/standort/benutzer/katalog'),
  list: () => api.get<RbacBenutzer[]>('/api/standort/benutzer'),
  addZugang: (email: string, standortId: number) =>
    api.post<RbacBenutzer>('/api/standort/benutzer/zugang', { email, standort_id: standortId }),
  removeZugang: (accountId: string, standortId: number) =>
    api.delete<RbacBenutzer>(`/api/standort/benutzer/${accountId}/zugang/${standortId}`),
  setZugangAktiv: (accountId: string, standortId: number, aktiv: boolean) =>
    api.put<RbacBenutzer>(`/api/standort/benutzer/${accountId}/zugang/${standortId}`, { aktiv }),
  setRollen: (
    accountId: string,
    standortId: number,
    payload: { standort_team: boolean; standortrollen: string[]; teamrollen: string[] | null },
  ) => api.put<RbacBenutzer>(`/api/standort/benutzer/${accountId}/rollen/${standortId}`, payload),
  overrides: (accountId: string) =>
    api.get<Override[]>(`/api/standort/benutzer/${accountId}/overrides`),
  createOverride: (accountId: string, payload: OverridePayload) =>
    api.post<Override>(`/api/standort/benutzer/${accountId}/overrides`, payload),
  deactivateOverride: (accountId: string, overrideId: string, reason: string) =>
    api.delete<Override>(`/api/standort/benutzer/${accountId}/overrides/${overrideId}`, {
      headers: reasonHeader(reason),
    }),
};

/** Bezeichnungen der Bestandsrollen, nur für den Hinweis auf noch nicht übernommene Konten. */
export const ALTROLLEN_LABELS: Record<string, string> = {
  admin: 'Super-Admin (alt)',
  standort_admin: 'Standort-Admin (alt)',
  office: 'Verwaltung (alt)',
  verwaltung: 'Verwaltung (alt)',
  assistant: 'Assistenz (alt)',
  assistent: 'Assistenz (alt)',
};

export const benutzerName = (u: Pick<RbacBenutzer, 'vorname' | 'nachname' | 'email'>): string =>
  `${u.vorname} ${u.nachname}`.trim() || u.email;
